import * as ko from "knockout";

import { AnonymousSubscription } from "rxjs/Subscription";

/** Union type of Disposable and Rx Subscription. */
export type UnifiedDisposable = Disposable | AnonymousSubscription;

/**
 * Disposes a Disposable or Subscription instance.
 */
function dispose(d: UnifiedDisposable): void {
    if (isDisposable(d)) {
        d.dispose();
    } else if (isSubscription(d)) {
        d.unsubscribe();
    }
}

/**
 * Disposable that runs the specified action in the specified
 * (optional) "this" context during disposal.
 */
export class ActionDisposable implements Disposable {
    constructor(action: () => void, context?: any) {
        this._action = action;
        this._context = context;
    }

    dispose(): void {
        this._action.call(this._context);
    }

    private _action: () => void;
    private _context: any;
}

/**
 * Represents a group of disposable resources that are disposed together (in order or registration).
 */
export class CompositeDisposable implements Disposable {
    constructor(...disposables: UnifiedDisposable[]) {
        this._disposables = disposables.filter(d => d && (isDisposable(d) || isSubscription(d)));
    }

    add<TDisposable extends UnifiedDisposable>(disposable: TDisposable): TDisposable {
        if (disposable) {
            if (this._disposed) {
                dispose(disposable);
            } else {
                this._disposables.push(disposable);
            }
        }
        return disposable;
    }

    dispose(): void {
        this._disposed = true;
        for (const d of this._disposables) {
            dispose(d);
        }
    }

    private _disposed: boolean = false;
    private _disposables: UnifiedDisposable[]; // initialized in constructor
}

/**
 * Provides a mechanism for releasing resources.
 */
export interface Disposable {

    /**
     * Disposes this instance (releases resources it uses).
     */
    dispose(): void;

}

/**
 * Call "dispose" function recursively on all objects that define it.
 *
 * If an object has a "dispose" method that returns boolean value "false",
 * "disposeObject" will enumerate that object's properties and attempt
 * to dispose them recursively. If "dispose" method returns anything
 * else, no enumeration/automatic recursive disposal will take place
 * for that object (this is the standard behaviour).
 *
 * @param o
 *     Object to dispose.
 * @param force
 *     If falsy, "function" containers (i.e. properties of type "function"
 *     that contain child properties, e.g. the "extras" property used by
 *     "ViewModelInit.js" to prevent JSON serialization) will be ignored.
 *     Otherwise ("force" is truthy) "function" containers will be included
 *     in traversal and all their properties will be disposed.
 * @returns
 *     The disposed object.
 */
export function disposeObject(o: any, force: boolean = false): any {
    if (o) {
        if (!o.dispose || (o.dispose() === false)) { // if "dispose()" method exists but returns "false", treat it as though it didn't exist (useful if you want to do an action IN ADDITION to standard disposal)
            if (ko.isObservable(o)) { // don't traverse child properties of knockout functions
                if (o.indexOf) { // (naive detection of observableArray)
                    disposeObject(o(), force); // in case of observableArray we want to dispose each item in the array (as opposed to properties of the observableArray function), thus "o()"
                }
            } else if ((force && typeof o === "function") || typeof o === "object") {
                // because "extras" can be declared as non-enumerable
                // property (using "Object.defineProperty", e.g. in
                // DocumentViewModel base class), the loop below
                // will not catch it and we need to check for it
                // manually to preserve the original "force" semantics
                const skipExtrasInLoop = !!(force && o.extras);
                for (const k in o) {
                    if (o.hasOwnProperty(k)) {
                        if (!skipExtrasInLoop || k !== "extras") {
                            disposeObject(o[k], force);
                        }
                    }
                }
                if (skipExtrasInLoop) {
                    disposeObject(o.extras, force);
                }
            }
        }
    }
    return o;
}

/**
 * Type guard for compatibility with "Disposable".
 */
export function isDisposable(d: any): d is Disposable {
    return typeof d.dispose === "function";
}

/**
 * Type guard for compatibility with "AnonymousSubscription".
 */
export function isSubscription(s: any): s is AnonymousSubscription {
    return typeof s.unsubscribe === "function";
}

/**
 * Disposes the specified guarded value after the specified action finishes (whether an exception is thrown or not).
 */
export function using<TDisposable extends UnifiedDisposable, TResult>(guardedValue: TDisposable, action: (guardedValue: TDisposable) => TResult): TResult {
    if (guardedValue !== null && guardedValue !== undefined && !isDisposable(guardedValue) && !isSubscription(guardedValue)) {
        throw new Error("using: Guarded value must implement \"dispose\" or \"unsubscribe\" method");
    }

    let result: TResult = undefined;
    try {
         result = action(guardedValue);
    }
    finally {
        if (guardedValue) {
            dispose(guardedValue);
        }
    }

    return result;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/disposable.ts